<script setup>
import SaveAppointmentFrequencyAction from '@frontJs/components/actions/projectManagement/apikom/SaveAppointmentFrequencyAction.vue';
import TenantAppointmentCell from '@frontJs/components/tables/cells/projectManagement/apikom/tenantReports/TenantAppointmentCell.vue';
import ClockIcon from '@iconify-vue/ph/clock';
import EnvelopeIcon from '@iconify-vue/ph/envelope';
import EyeIcon from '@iconify-vue/ph/eye';
import PlusIcon from '@iconify-vue/ph/plus';
import SendEmailAction from '@js/components/actions/SendEmailAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { toastSaved, toastSuccess } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const appointmentFrequencyLabels = {
    monthly: 'Mensuel',
    bimonthly: 'Bimensuel',
    quarterly: 'Trimestriel',
    semiannual: 'Semestriel',
    yearly: 'Annuel',
};

const { forthcoming, makeAnAppointment } = defineProps({
    forthcoming: {
        type: Boolean,
        required: false,
        default: false,
    },
    makeAnAppointment: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route('front.projectManagement.apikom.appointments', {
                        tenant: props.row.original.id,
                    }),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastLoginDate',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière connexion',
                }),
            cell: (props) =>
                props.row.original.lastLoginDate
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.lastLoginDate,
                              false,
                              true,
                          ),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'appointmentFrequency',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Récurrence RDV',
                }),
            cell: (props) =>
                props.row.original.appointmentFrequency
                    ? h(PillCell, {
                          pillValue:
                              appointmentFrequencyLabels[
                                  props.row.original.appointmentFrequency
                              ],
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastContactAppointment',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernier RDV',
                }),
            cell: (props) =>
                props.row.original.lastContactAppointment
                    ? h(TenantAppointmentCell, {
                          appointment:
                              props.row.original.lastContactAppointment,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'nextAppointment',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prochain RDV',
                }),
            cell: (props) =>
                props.row.original.nextAppointment
                    ? h(TenantAppointmentCell, {
                          appointment: props.row.original.nextAppointment,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'appointmentMailRequestSentAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Email',
                }),
            cell: (props) =>
                props.row.original.appointmentMailRequestSentAt
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.appointmentMailRequestSentAt,
                          ),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(
                                SaveAppointmentFrequencyAction,
                                {
                                    tenant: props.row.original,
                                    callback: () => {
                                        toastSaved();
                                        updateData();
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            type: 'secondary',
                                            title: 'Mettre à jour la fréquence des RDV',
                                            icon: ClockIcon,
                                        }),
                                },
                            ),
                            h(
                                SendEmailAction,
                                {
                                    fromOptions:
                                        props.row.original.email.fromOptions,
                                    toOptions:
                                        props.row.original.email.toOptions,
                                    to: props.row.original.email.toOptions,
                                    subject: props.row.original.email.subject,
                                    body: props.row.original.email.body,
                                    callback: (
                                        from,
                                        to,
                                        subject,
                                        body,
                                        setLoading,
                                        setIsOpen,
                                    ) => {
                                        axios
                                            .post(
                                                route(
                                                    'front.projectManagement.apikom.postSendTenantAppointmentEmail',
                                                    {
                                                        tenant: props.row
                                                            .original.id,
                                                    },
                                                ),
                                                { from, to, subject, body },
                                            )
                                            .then(() => {
                                                setLoading(false);
                                                setIsOpen(false);
                                                toastSuccess(
                                                    'Envoyé avec succès.',
                                                );
                                                updateData();
                                            })
                                            .catch((error) => {
                                                console.error(error);
                                            });
                                    },
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            type: 'primary',
                                            title: 'Envoyer un email',
                                            icon: EnvelopeIcon,
                                        }),
                                },
                            ),
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Voir',
                                href: route(
                                    'front.projectManagement.apikom.appointments',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                icon: EyeIcon,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.apikom.postAppointments'),
    data: {
        forthcoming,
        makeAnAppointment,
    },
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        nextAppointment: makeAnAppointment !== true,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.apikom.createAppointment')
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
